'use client';

import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import { getAvailableLanguages, getMessagesForLocale } from '@/i18n/client';
import { defaultLocale } from '@/i18n/config';
import { writeLanguageCookie, writeLanguageExplicitCookie } from '@/i18n/languageCookies';
import { ELocalStorageKeys } from '@/lib/enums';
import { SupportedLanguage } from '@/lib/types';
import { useLongLoadingRum } from '@/hooks/useLongLoadingRum';
import { resolveLanguage, readGeoCookie, readIsLanguageExplicitlySet } from '@web-store-platforms/locales';
import { useAppState } from '@/state/hooks/app.state.hook';
import { webStorageUtil } from '@/state/webStorage.state.service';

import { NextIntlClientProvider } from 'next-intl';

import { INTL_TIME_ZONE, createMessageFallback, handleIntlError } from '@/i18n/utils';

/**
 * A language change fetches the new messages before switching, and that fetch
 * has no upper bound of its own: the translation API calls carry no timeout.
 * Stop showing the global loader past this point; a later arrival still applies.
 */
const TRANSLATION_WAIT_LIMIT_MS = 5000;

interface ILocaleProviderProps {
  children: ReactNode;
  /** The locale the server rendered in. */
  initialLocale: string;
  /** Its messages, resolved on the server: the document renders in them with nothing to wait for. */
  initialMessages: Record<string, unknown>;
}

export const LocaleProvider: FC<ILocaleProviderProps> = ({
  children,
  initialLocale,
  initialMessages,
}) => {
  const [messages, setMessages] = useState(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const {
    languagesList,
    setCurrentLanguage,
    currentLanguage,
    initialBoot,
    setIsGlobalLoading,
  } = useAppState();
  const router = useRouter();

  const publisherId =
    webStorageUtil.get(ELocalStorageKeys.CURRENT_STORE_ID) || initialBoot?.stores?.[0];

  useLongLoadingRum({
    context: { currentLanguage },
    isLoading,
    loadingArea: 'translations',
    thresholdMilliseconds: TRANSLATION_WAIT_LIMIT_MS,
  });

  useEffect(() => {
    const loadAvailableLanguages = async () => {
      if (publisherId && !languagesList?.length) {
        try {
          await getAvailableLanguages(publisherId);
        } catch (error) {
          console.error('Failed to load available languages:', error);
        }
      }
    };

    loadAvailableLanguages();
  }, [publisherId, languagesList?.length]);

  useEffect(() => {
    if (!languagesList?.length || !currentLanguage) return;

    const geoCountryCode = readGeoCookie();
    const isExplicitlySelected = readIsLanguageExplicitlySet();
    const savedLanguage = webStorageUtil.get(ELocalStorageKeys.CURRENT_LANGUAGE);

    // Keep the server-readable mirror of the flag current for players who
    // picked a language before the cookie existed.
    if (isExplicitlySelected) writeLanguageExplicitCookie();

    const resolved = resolveLanguage({
      savedLanguage,
      isExplicitlySelected,
      geoCountryCode,
      bootLocale: initialBoot?.locale ?? null,
      supportedLanguages: languagesList as SupportedLanguage[],
    });

    if (resolved !== currentLanguage) {
      setCurrentLanguage(resolved);
      webStorageUtil.set(ELocalStorageKeys.CURRENT_LANGUAGE, resolved);
      writeLanguageCookie(resolved);
    }
  }, [languagesList, currentLanguage, setCurrentLanguage, initialBoot?.locale]);

  // The language whose messages are currently rendered.
  const loadedLanguageRef = useRef(initialLocale);

  useEffect(() => {
    if (!publisherId || !currentLanguage || currentLanguage === loadedLanguageRef.current) {
      return;
    }

    let isCancelled = false;

    const stopWaiting = setTimeout(() => {
      if (!isCancelled) {
        setIsLoading(false);
        setIsGlobalLoading(false);
      }
    }, TRANSLATION_WAIT_LIMIT_MS);

    const loadLocaleData = async () => {
      try {
        setIsLoading(true);
        setIsGlobalLoading(true);

        const nextMessages = await getMessagesForLocale(currentLanguage, publisherId);

        if (isCancelled) return;
        setMessages(nextMessages);
        loadedLanguageRef.current = currentLanguage;

        router.refresh();
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load locale data:', error);
        }
      } finally {
        clearTimeout(stopWaiting);

        if (!isCancelled) {
          setIsLoading(false);
          setIsGlobalLoading(false);
        }
      }
    };

    loadLocaleData();

    return () => {
      isCancelled = true;
      clearTimeout(stopWaiting);
    };
  }, [publisherId, currentLanguage]);

  const handleMessageFallback = useMemo(
    () => createMessageFallback(messages),
    [messages],
  );

  return (
    <NextIntlClientProvider
      locale={currentLanguage || defaultLocale.isoCode}
      messages={messages}
      timeZone={INTL_TIME_ZONE}
      onError={handleIntlError}
      getMessageFallback={handleMessageFallback}
    >
      {children}
    </NextIntlClientProvider>
  );
};
